// Abstracted here from CroppingImageUploadModal and draw_placeholder_image in learn-webapp
/**
 * Uses the natural dimensions and the end dimensions of an image to determine how the image is currently scaled, and defines the cropDetails to match.
 * @param image The image as is currently rendered, even temporarily.
 * @param cropDetails The desired cropDetails.
 */
export const scaleCropDetailsToImage = (image, cropDetails) => {
    const { width, height, naturalWidth, naturalHeight } = image;
    const scaleX = naturalWidth / width;
    const scaleY = naturalHeight / height;
    return {
        x: Math.floor(cropDetails.x * scaleX),
        y: Math.floor(cropDetails.y * scaleY),
        width: Math.floor(cropDetails.width * scaleX),
        height: Math.floor(cropDetails.height * scaleY)
    };
};
/**
 * Draws the end cropped image with the appropriate scale. The options object can be used to interact with the canvas context as it is being built, if needed.
 * @param image The image as is currently rendered, even temporarily.
 * @param cropDetails The desired cropDetails.
 * @param scaledCropDetails The scaled cropDetails, @see scaleCropDetailsToImage.
 * @param options Optional interactions with the canvas context, this can be used to make alterations to the end image (like giving it a white background).
 */
export const drawScaledCroppedImage = (image, cropDetails, scaledCropDetails, options = {}) => {
    const { onContextCreate } = options;
    const canvas = document.createElement('canvas');
    canvas.width = cropDetails.width;
    canvas.height = cropDetails.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
        throw new Error('Canvas failed to get 2d context');
    }
    ctx.clearRect(0, 0, scaledCropDetails.width, scaledCropDetails.height);
    if (onContextCreate)
        onContextCreate(ctx);
    ctx.drawImage(image, scaledCropDetails.x, scaledCropDetails.y, scaledCropDetails.width, scaledCropDetails.height, 0, 0, scaledCropDetails.width, scaledCropDetails.height);
    const base64Image = canvas.toDataURL('image/png'); // Default to PNG to support transparent image uploads
    return base64Image;
};
/**
 * Given a provided imageSrc, it is scaled and cropped and returned as base64
 * @param imageSrc The address or URL of the a media resource that is to be considered.
 * @param cropDetails Desired width, height, x, and y for the image, @see CropDetails.
 * @param options Optional interactions with the canvas context, this can be used to make alterations to the end image (like giving it a white background).
 */
const cropImage = (imageSrc, cropDetails, options = {}) => {
    if (!imageSrc) {
        throw new Error('No imageSrc provided to cropImage');
    }
    if (!cropDetails) {
        throw new Error('No cropDetails provided to cropImage');
    }
    const tmpImage = document.createElement('img');
    return new Promise((resolve, reject) => {
        let hasRejected = false;
        const performRejection = (error) => {
            if (hasRejected) {
                return;
            }
            hasRejected = true;
            return reject(error);
        };
        fetch(imageSrc, { mode: 'cors' })
            .then((result) => result.blob())
            .then((imageData) => {
            tmpImage.src = URL.createObjectURL(imageData);
        })
            .catch((error) => {
            return performRejection(error);
        });
        tmpImage.onload = function () {
            const scaledCropDetails = scaleCropDetailsToImage(tmpImage, cropDetails);
            try {
                const croppedImageAsBase64 = drawScaledCroppedImage(tmpImage, cropDetails, scaledCropDetails, options);
                return resolve(croppedImageAsBase64);
            }
            catch (error) {
                return performRejection(error);
            }
        };
        tmpImage.onerror = (error) => {
            return performRejection(error);
        };
    });
};
export default cropImage;
//# sourceMappingURL=cropImage.js.map